import { CircularProgress, Tooltip } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputBase from '@material-ui/core/InputBase';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import CloseIcon from '@material-ui/icons/Close';
import GroupIcon from '@material-ui/icons/Group';
import MapIcon from '@material-ui/icons/Map';
import SearchIcon from '@material-ui/icons/Search';
import downloadIcon from 'assets/excel.png';
import Button from 'components/Button';
import format from 'date-fns/format';
import * as FileSaver from 'file-saver';
import WIRField from 'pages/WellGuardian/WIR/components/WIRField';
import { getIsSuperUser, getRoleName } from 'pages/WellGuardian/WIR/utils';
import { NOT_APPLICABLE } from 'pages/WellGuardian/WIR/wir.constants';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelectUser } from 'redux/User/selectors';
import { useCountryState, useWIRUsers } from 'redux/WellGuardian/selectors';
import XLSX from 'sheetjs-style';
import { AssetType, WIRTransverseRoles, WIRUser } from 'types/wellGuardian';

import styles from './UserRoleSettingsModal.module.scss';
import RemoveUserConfirmationModal from './components/RemoveUserConfirmationModal';
import UserDetailsModal from './components/UserDetailsModal';
import UserDetailsModalTable from './components/UserDetailsModalTable';
import { useUserRoleSettingsModalData } from './hooks';
import { getAllWirRoles, getUserRole } from './utils';
import { getAssetsByCountry } from '../utils/getAssetsByCountry';

type UserRoleSettingsModalProps = {
  modalOpen: boolean;
  cancelMethod: () => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
};

const ALL_WIR = 'ALL';

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

const fileExtension = '.xlsx';

const PDF_DATE_FORMAT = 'yyyy-MM-dd';

const UserRoleSettingsModal: React.FunctionComponent<UserRoleSettingsModalProps> = ({
  modalOpen,
  cancelMethod,
  searchText,
  setSearchText,
}) => {
  const intl = useIntl();
  const {
    okAPIMethod,
    saverUser,
    deleteUser,
    user,
    setUser,
    mode,
    setMode,
    openUserDetailsModal,
    setOpenUserDetailsModal,
    openRemoveUserConfirmationModal,
    setOpenRemoveUserConfirmationModal,
    allWirUsers,
    wirUsersForPage,
    page,
    setPage,
    pageSize,
    usersLoading,
    assetTypeFilter,
    setAssetTypeFilter,
    roleTypeFilter,
    setRoleTypeFilter,
    excelData,
    setExcelData,
    multiUser,
    setMultiUser,
    setFinalExcelData,
  } = useUserRoleSettingsModalData();
  const countryState = useCountryState();
  const onSearchInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearchText(e.target.value as string);
  };
  const userA = useSelectUser();
  const users = useWIRUsers();
  const isSuperUser = getIsSuperUser(users, userA, countryState);

  const onClearSearch = () => {
    setSearchText('');
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleUserAddClick = () => {
    setMode('add');
    setUser({});
    setOpenUserDetailsModal(true);
  };

  const handleUserEditSelect = (selectedUser: WIRUser) => {
    setMode('edit');
    setUser(selectedUser);
    setOpenUserDetailsModal(true);
  };

  const handleUserDeleteSelect = (selectedUser: WIRUser) => {
    setUser(selectedUser);
    setOpenRemoveUserConfirmationModal(true);
  };

  const resetUserDetailsModal = () => {
    if (!multiUser) {
      setUser({});
      setOpenUserDetailsModal(false);
    } else {
      setUser({});
      setOpenUserDetailsModal(false);
      setMultiUser(false);
      setExcelData(null);
    }
  };

  const getUserRoleMenu = () => {
    const asset = getAssetsByCountry(countryState);

    return Object.keys(asset).map((eKey, id) => (
      <MenuItem dense value={eKey} key={eKey}>
        {intl.formatMessage({ id: `wellGuardian.assets.${Object.values(asset)[id]}` })}
      </MenuItem>
    ));
  };

  const additionData = allWirUsers?.map(item => ({
    ...item,
    'USER ID': item.userId,
    'USER NAME': item.userName,
    'EMAIL ADDRESS': item.emailAddress,
    ROLES: getUserRole(item.roles),
    ASSETS: item?.assets.indexOf(ALL_WIR) === -1 ? item?.assets?.join(', ') : ALL_WIR,
    COUNTRY: item.country,
  }));

  const additionDataFinal = additionData.map(
    ({ id, userId, userName, emailAddress, roles, assets, country, ...rest }) => rest,
  );

  const exportToExcel = async () => {
    const ws = XLSX.utils.json_to_sheet(additionDataFinal);
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(
      data,
      `USER ROLES LIST - ${format(new Date(), PDF_DATE_FORMAT)}` + fileExtension,
    );
  };

  const handleFileUpload = (e: any) => {
    setExcelData(null);

    const file = e.target?.files[0];
    const reader = new FileReader();

    reader.onload = (event: any) => {
      const workbook = XLSX.read(event.target.result, { type: 'binary' });
      const sheetName = workbook?.SheetNames[0];
      const sheet = workbook?.Sheets[sheetName];
      const sheetData = XLSX.utils.sheet_to_json(sheet);

      const processedData = sheetData.map((user: any) => ({
        ...user,
        assets: user.assets ? user.assets.split(', ') : ['ALL'],
        roles: user.roles ? [user.roles] : [WIRTransverseRoles.READ_ONLY_WIR_USER],
        country: user.country ? user.country : countryState,
      }));

      const groupedByEmail: Record<string, any[]> = processedData.reduce((acc, user) => {
        const email = user.emailAddress;
        if (!acc[email]) {
          acc[email] = [];
        }
        acc[email].push(user);

        return acc;
      }, {});

      const highlightedData = processedData.map(user => ({
        ...user,
        isDuplicate: groupedByEmail[user.emailAddress].length > 1,
      }));

      setExcelData(highlightedData);

      const nonMatchingData = highlightedData.filter(
        (item: any) =>
          !allWirUsers?.some((prefItem: any) => prefItem?.emailAddress === item?.emailAddress),
      );

      setFinalExcelData(nonMatchingData);
      setUser({});
      setOpenUserDetailsModal(false);
    };

    reader.readAsBinaryString(file);
  };

  return (
    <Dialog
      aria-labelledby="user-role-settings-dialog"
      open={modalOpen}
      maxWidth="xl"
      scroll="body"
      classes={{ paperWidthXl: styles.paperWidthXl, root: styles.root }}
      data-testid="user-role-settings-dialog"
    >
      <MuiDialogTitle disableTypography className={styles.titleDialog}>
        <Typography variant="h6" className={styles.titleText}>
          {intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.userRoleSettingsTitle' })}
        </Typography>
        <IconButton
          aria-label="close"
          className={styles.closeButton}
          onClick={() => {
            cancelMethod();
            setAssetTypeFilter('ALL' as AssetType);
            setMultiUser(false);
            setExcelData(null);
          }}
        >
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
      <MuiDialogContent classes={{ root: styles.contentRoot }} dividers>
        {usersLoading ? (
          <div className={styles.alignCenter}>
            <CircularProgress size={50} color="inherit" />
          </div>
        ) : (
          <>
            {!multiUser && (
              <div className={styles.userRoleFilters}>
                <div className={styles.filterField}>
                  <WIRField
                    id="asset-field-outlined"
                    label={intl.formatMessage({
                      id: 'wellGuardian.wir.userRoleSettings.assetType',
                    })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MapIcon />
                        </InputAdornment>
                      ),
                      classes: {
                        adornedStart: styles.adornedStartSelect,
                      },
                      className: styles.fieldInput,
                    }}
                    onChange={event => {
                      setAssetTypeFilter(event.target.value as AssetType);
                    }}
                    value={assetTypeFilter}
                    select
                  >
                    {getUserRoleMenu()}
                  </WIRField>
                </div>
                <div className={styles.filterField}>
                  <WIRField
                    id="role-field-outlined"
                    label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.roleType' })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <GroupIcon />
                        </InputAdornment>
                      ),
                      classes: {
                        adornedStart: styles.adornedStartSelect,
                      },
                      className: styles.fieldInput,
                    }}
                    onChange={event => setRoleTypeFilter(event.target.value)}
                    value={roleTypeFilter}
                    select
                  >
                    <MenuItem dense value={NOT_APPLICABLE}>
                      {intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.allRoles' })}
                    </MenuItem>
                    <Divider />
                    {getAllWirRoles().map(role => (
                      <MenuItem dense value={role} key={role}>
                        {getRoleName(role)}
                      </MenuItem>
                    ))}
                  </WIRField>
                </div>
                <div className={styles.filterField}>
                  <InputBase
                    id="search-well"
                    className={styles.searchInput}
                    autoComplete="off"
                    value={searchText}
                    onChange={onSearchInputChange}
                    placeholder={intl.formatMessage({
                      id: 'wellGuardian.wir.userRoleSettings.searchPlaceholder',
                    })}
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    }
                    endAdornment={
                      <IconButton
                        data-testid="clear"
                        size="small"
                        className={styles.clearButton}
                        onClick={onClearSearch}
                      >
                        <ClearIcon fontSize="small" color="inherit" />
                      </IconButton>
                    }
                  />
                </div>
                <div className={styles.rightPart}>
                  <Tooltip title="Export User Roles to Excel">
                    <div
                      data-testid="export"
                      className={styles.downloadImage}
                      onClick={exportToExcel}
                    >
                      <div className={styles.downloadButton}>
                        <img src={downloadIcon} alt="" />
                      </div>
                    </div>
                  </Tooltip>
                  <Button onClick={handleUserAddClick} className={styles.linkButton}>
                    <AddIcon />
                    <span className={styles.linkButtonText}>
                      {intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.addUser' })}
                    </span>
                  </Button>
                </div>
              </div>
            )}
            <UserDetailsModalTable
              wirUsersForPage={wirUsersForPage}
              allWirUsers={allWirUsers}
              pageSize={pageSize}
              page={page}
              handlePageChange={handlePageChange}
              handleUserEditSelect={item => handleUserEditSelect(item)}
              handleUserDeleteSelect={item => handleUserDeleteSelect(item)}
              searchText={searchText}
              multiUser={multiUser}
              setMultiUser={setMultiUser}
              excelData={excelData}
              setExcelData={setExcelData}
              cancelMethod={resetUserDetailsModal}
              okAPIMethod={okAPIMethod}
              handleFileUpload={handleFileUpload}
            />
          </>
        )}
        <UserDetailsModal
          mode={mode}
          user={user}
          setUser={setUser}
          modalOpen={openUserDetailsModal}
          okMethod={saverUser}
          cancelMethod={resetUserDetailsModal}
          isSuperUser={isSuperUser}
          handleFileUpload={handleFileUpload}
          multiUser={multiUser}
          setMultiUser={setMultiUser}
        />
        <RemoveUserConfirmationModal
          userName={user.userName}
          modalOpen={openRemoveUserConfirmationModal}
          okMethod={deleteUser}
          cancelMethod={() => setOpenRemoveUserConfirmationModal(false)}
        />
      </MuiDialogContent>
    </Dialog>
  );
};

export default UserRoleSettingsModal;
